import { useEffect, useRef, useState } from "react";
import { api, ApiError } from "../../lib/api";
import { captureProfileGuard } from "../../lib/profileSession";
import type { ActivityTarget, ContractActivityDirection, ContractActivityPage } from "../../lib/api-client/contractActivity";

type HistorySnapshot = { key: string; page: ContractActivityPage | null };
type HistoryView = { page: ContractActivityPage; visibleDates: number };

/** Date disclosures need complete groups even when a transport page ends inside
 * one date. Read one date beyond the visible extent with the same snapshot cursor. */
export function useContractActivityHistory(target: ActivityTarget, direction: ContractActivityDirection, visibleDates: number, restart?: string) {
  const { id, kind } = target;
  const snapshot = useRef<HistorySnapshot>({ key: "", page: null });
  const [view, setView] = useState<HistoryView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  useEffect(() => {
    let current = true;
    const assertCurrent = captureProfileGuard();
    const key = `${kind}:${id}:${direction}:${restart ?? ""}`;
    if (snapshot.current.key !== key) {
      snapshot.current = { key, page: null };
      setView(null);
    }
    setLoading(true); setError(null);
    async function read() {
      let history = snapshot.current.page;
      const cursors = new Set<string>();
      while (!history || (history.nextCursor && new Set(history.events.map(event => event.actionDate).filter(Boolean)).size <= visibleDates)) {
        const cursor = history?.nextCursor;
        if (cursor && cursors.has(cursor)) throw new Error("History pagination did not advance.");
        if (cursor) cursors.add(cursor);
        const next = await api.activity({ kind, id }, direction, cursor, restart);
        assertCurrent();
        if (!current) return;
        const events = new Map(history?.events.map(event => [event.id, event]) ?? []);
        next.events.forEach(event => events.set(event.id, event));
        history = { ...next, events: [...events.values()] };
        snapshot.current = { key, page: history };
      }
      if (current && history) setView({ page: history, visibleDates });
    }
    void read().catch((problem: unknown) => {
      if (!current || (problem instanceof DOMException && problem.name === "AbortError")) return;
      const denied = problem instanceof ApiError && [401, 403, 404].includes(problem.status);
      if (denied) snapshot.current = { key, page: null };
      setView(null);
      setError(denied ? "This Activity is no longer available in the selected account or profile."
        : "Could not load Activity. Saved history may still exist. Please retry.");
    }).finally(() => { if (current) setLoading(false); });
    return () => { current = false; };
  }, [id, kind, direction, visibleDates, retryCount, restart]);
  return { page: view?.page ?? null, shownDates: view?.visibleDates ?? 0, error, loading, retry: () => {
    snapshot.current = { key: "", page: null }; setView(null); setLoading(true); setRetryCount(value => value + 1);
  } };
}
